/**
 * The make-up air unit — the outside-air half of the cleanroom's air loop.
 *
 * The recirculation handlers (the AHU skid, `hvac` in the house model) move
 * the cleanroom's own air round and round through the filters. They cannot
 * set its humidity, replace what the exhaust fans throw away, or hold the
 * room above atmospheric pressure. That is this unit's job: take outside air
 * at whatever the weather is doing, and hand the room air at one temperature
 * and one humidity, in slightly more quantity than the exhaust takes out.
 *
 * Three ideas, each a working habit as much as a piece of physics:
 *
 * DEHUMIDIFYING IS DONE WITH THE COOLING COIL, AND IT COSTS REHEAT. Air
 * holds less water when cold, so the only way to take water out of summer
 * air is to cool it to the dew point the room wants - which is far below the
 * temperature the room wants - and then heat it back up. On a humid day the
 * unit is running its cooling coil AND its heating coil at once, and that is
 * not a fault; a technician who "fixes" it by shutting the reheat has just
 * put a cold, wet supply into the cleanroom.
 *
 * WINTER IS A HUMIDIFICATION PROBLEM. Cold outside air is nearly dry in
 * absolute terms even at high relative humidity: warm it to 20 °C and its
 * relative humidity collapses to a few percent, which is where static
 * discharge starts killing devices. The steam humidifier is a winter load,
 * and it is a big one.
 *
 * PRESSURE IS A FLOW BALANCE, NOT A SETTING. The cleanroom sits above
 * atmospheric because more air is pushed in than the exhaust and the leaks
 * take out; the difference squeezes out through every crack, and the
 * pressure is whatever drives that flow. Turn the exhaust up without the
 * make-up air and the room goes negative - and the dirty corridor breathes
 * in through the same cracks.
 *
 * The psychrometrics are the standard ones (Magnus saturation curve, the
 * 0.622 ratio, the enthalpy of moist air). The capacities and the leakage
 * area are ONE plausible unit, declared here as design data so the harness
 * can check the design day lands inside them. The house model carries this
 * unit's delivery and the room pressure it comes to (`mauHouse`), which is
 * how a loaded pre-filter down here reaches the gowning bay upstairs.
 */

/** Standard atmospheric pressure [Pa]. */
export const P_ATM = 101325
/** Air density at cleanroom conditions [kg/m³]. */
export const RHO_AIR = 1.2

export interface MauSettings {
  /** Outside air dry-bulb temperature [°C]. */
  outsideC: number
  /** Outside air relative humidity [%]. */
  outsideRh: number
  /** Supply air setpoint [°C]. */
  supplyC: number
  /** Supply air relative humidity setpoint [%]. */
  supplyRh: number
  /** Supply fan, percent of design flow. */
  fanPct: number
  /** Process exhaust fans, percent of design flow. */
  exhaustPct: number
}

/** The unit's rating plate. */
export const MAU_DESIGN = {
  /** Supply air at 100 % fan [m³/h]. */
  flowM3h: 12000,
  /** Process exhaust the room loses at 100 % [m³/h]. */
  exhaustM3h: 9000,
  /** Fan shaft power at design flow [kW]. */
  fanKw: 15,
  /** Chilled-water coil capacity [kW]. */
  coolingKw: 240,
  /** Hot-water coils (preheat and reheat together) [kW]. */
  heatingKw: 130,
  /**
   * The preheat coil holds the air entering the cooling coil at least this
   * warm, so a winter morning cannot freeze the chilled-water coil - the
   * classic way to split a make-up air unit's casing.
   */
  preheatC: 5,
  /** Steam humidifier capacity [kg/h]. */
  steamKgH: 110,
  /**
   * Effective leakage area of the cleanroom envelope, Cd·A [m²]. Chosen so
   * the design flows hold the room at +15 Pa, the usual cleanroom cascade.
   */
  leakageM2: 0.1667,
  /** Chiller coefficient of performance, for the power line. */
  chillerCop: 3.5,
} as const

export const DEFAULT_MAU: MauSettings = {
  outsideC: 32,
  outsideRh: 60,
  supplyC: 20,
  supplyRh: 45,
  fanPct: 100,
  exhaustPct: 100,
}

/** Outside-air design days a technician can pull up in one click. */
export const MAU_WEATHER: Record<'summer' | 'winter' | 'mild', Pick<MauSettings, 'outsideC' | 'outsideRh'>> = {
  summer: { outsideC: 32, outsideRh: 60 },
  winter: { outsideC: -5, outsideRh: 70 },
  mild: { outsideC: 18, outsideRh: 55 },
}

/** Slider ranges, in the kit's knob shape; the labels are the catalogue fallbacks. */
export const MAU_LIMITS: Record<keyof MauSettings, { min: number; max: number; step: number; unit: string; label: string }> = {
  outsideC: { min: -20, max: 40, step: 1, unit: '°C', label: 'Outside air temperature' },
  outsideRh: { min: 10, max: 100, step: 5, unit: '%', label: 'Outside air humidity' },
  supplyC: { min: 16, max: 26, step: 0.5, unit: '°C', label: 'Supply air setpoint' },
  supplyRh: { min: 30, max: 60, step: 5, unit: '% RH', label: 'Supply humidity setpoint' },
  fanPct: { min: 50, max: 120, step: 5, unit: '%', label: 'Supply fan, % of design' },
  exhaustPct: { min: 0, max: 150, step: 5, unit: '%', label: 'Process exhaust, % of design' },
}

// ---------------------------------------------------------- psychrometrics

/** Saturation vapour pressure over water [Pa], Magnus form (Alduchov & Eskridge). */
export function saturationPressurePa(tC: number): number {
  return 610.94 * Math.exp((17.625 * tC) / (tC + 243.04))
}

/** Humidity ratio [kg water / kg dry air] at a dry-bulb and relative humidity. */
export function humidityRatio(tC: number, rhPct: number): number {
  const pv = (rhPct / 100) * saturationPressurePa(tC)
  return (0.622 * pv) / (P_ATM - pv)
}

/** Dew point [°C] of air carrying this humidity ratio. */
export function dewPointC(w: number): number {
  const pv = (w * P_ATM) / (0.622 + w)
  const a = Math.log(pv / 610.94)
  return (243.04 * a) / (17.625 - a)
}

/** Relative humidity [%] of air at a dry-bulb carrying this humidity ratio. */
export function relativeHumidityPct(tC: number, w: number): number {
  const pv = (w * P_ATM) / (0.622 + w)
  return Math.min(100, (100 * pv) / saturationPressurePa(tC))
}

/** Specific enthalpy of moist air [kJ/kg dry air]. */
export function enthalpyKJkg(tC: number, w: number): number {
  return 1.006 * tC + w * (2501 + 1.86 * tC)
}

// ------------------------------------------------------------------ the unit

/**
 * The unit's own condition - what can be wrong with it, as opposed to what
 * it is set to. The house fault that reaches the cleanroom is written here,
 * and the bench shows it: an armed fault derates the unit the technician is
 * looking at.
 */
export interface MauCondition {
  /**
   * Pre-filter loading, 0 clean to 1 blinded, as the fraction of design flow
   * the loaded filters take away at the same fan speed.
   */
  filterLoading: number
}

export const NOMINAL_MAU_CONDITION: MauCondition = { filterLoading: 0 }

export type MauWarning =
  | 'coilFreeze'
  | 'coolingCapacity'
  | 'heatingCapacity'
  | 'steamCapacity'
  | 'pressureLow'
  | 'pressureHigh'

export interface MauReport {
  /** Supply air mass flow [kg/s]. */
  massFlowKgS: number
  /** Supply air actually delivered, as a fraction of design flow. */
  deliveredFraction: number
  supplyM3h: number
  exhaustM3h: number
  /** Outside and supply humidity ratios [kg/kg]. */
  outsideW: number
  supplyW: number
  /** Where the cooling coil leaves the air [°C] - the supply dew point on a humid day. */
  coilLeavingC: number
  /** The supply air's dew point [°C]. */
  supplyDewPointC: number
  /** True when the coil is wringing water out, not only cooling. */
  dehumidifying: boolean
  coolingKw: number
  heatingKw: number
  steamKgH: number
  fanKw: number
  /** Electrical-equivalent power: chiller at its COP, hot water direct, fan. */
  powerKw: number
  /** Cleanroom pressure relative to ambient [Pa]. */
  cleanroomPa: number
  warnings: MauWarning[]
}

export function runMau(s: MauSettings, condition: MauCondition = NOMINAL_MAU_CONDITION): MauReport {
  // Loaded pre-filters take flow away at the same fan speed: the fan works
  // against more resistance and moves less air, and the balance against the
  // exhaust - which the filters do nothing to - is what goes.
  const deliveredFraction = (s.fanPct / 100) * (1 - Math.min(1, Math.max(0, condition.filterLoading)))
  const supplyM3h = MAU_DESIGN.flowM3h * deliveredFraction
  const exhaustM3h = (MAU_DESIGN.exhaustM3h * s.exhaustPct) / 100
  const massFlowKgS = (RHO_AIR * supplyM3h) / 3600

  const outsideW = humidityRatio(s.outsideC, s.outsideRh)
  const supplyW = humidityRatio(s.supplyC, s.supplyRh)
  const supplyDewPointC = dewPointC(supplyW)
  const hOutside = enthalpyKJkg(s.outsideC, outsideW)

  let coilLeavingC: number
  let coolingKw = 0
  let steamKgH = 0
  let dehumidifying = false
  let wAfterCoil: number
  /** Where the hot-water coils pick the air up from [°C]. */
  let heatFromC: number
  if (outsideW > supplyW) {
    // Too wet: cool to the supply dew point and let the water condense out.
    // The coil leaves saturated air at that dew point, carrying supplyW.
    dehumidifying = true
    coilLeavingC = supplyDewPointC
    wAfterCoil = supplyW
    coolingKw = massFlowKgS * (hOutside - enthalpyKJkg(coilLeavingC, supplyW))
    heatFromC = coilLeavingC
  } else {
    // Dry enough: the coil only has sensible work, if any, and the
    // humidifier makes up the water. Cold air is preheated before it
    // reaches the chilled-water coil, which then has nothing to do.
    wAfterCoil = outsideW
    if (s.outsideC > s.supplyC) {
      coilLeavingC = s.supplyC
      coolingKw = massFlowKgS * (hOutside - enthalpyKJkg(s.supplyC, outsideW))
    } else {
      coilLeavingC = Math.max(s.outsideC, MAU_DESIGN.preheatC)
    }
    heatFromC = Math.min(s.outsideC, s.supplyC)
    steamKgH = massFlowKgS * (supplyW - outsideW) * 3600
  }
  // Preheat and reheat together, from wherever the air is to the setpoint -
  // which on a humid day is heating air the unit has just paid to cool.
  const heatingKw =
    heatFromC < s.supplyC
      ? massFlowKgS * (enthalpyKJkg(s.supplyC, wAfterCoil) - enthalpyKJkg(heatFromC, wAfterCoil))
      : 0

  // Fan laws: power goes as the cube of flow.
  const fanKw = MAU_DESIGN.fanKw * Math.pow(s.fanPct / 100, 3)
  const powerKw = coolingKw / MAU_DESIGN.chillerCop + heatingKw + fanKw

  // The envelope: net inflow squeezes out through the leakage area, and the
  // pressure is what drives that flow (orifice equation, solved for ΔP).
  const netM3s = (supplyM3h - exhaustM3h) / 3600
  const v = netM3s / MAU_DESIGN.leakageM2
  const cleanroomPa = Math.sign(v) * (RHO_AIR / 2) * v * v

  const warnings: MauWarning[] = []
  if (coilLeavingC < 3) warnings.push('coilFreeze')
  if (coolingKw > MAU_DESIGN.coolingKw) warnings.push('coolingCapacity')
  if (heatingKw > MAU_DESIGN.heatingKw) warnings.push('heatingCapacity')
  if (steamKgH > MAU_DESIGN.steamKgH) warnings.push('steamCapacity')
  if (cleanroomPa < 5) warnings.push('pressureLow')
  if (cleanroomPa > 30) warnings.push('pressureHigh')

  return {
    massFlowKgS,
    deliveredFraction,
    supplyM3h,
    exhaustM3h,
    outsideW,
    supplyW,
    coilLeavingC,
    supplyDewPointC,
    dehumidifying,
    coolingKw,
    heatingKw,
    steamKgH,
    fanKw,
    powerKw,
    cleanroomPa,
    warnings,
  }
}

/**
 * What the building feels from this unit: the three numbers the house model
 * carries for it. The house fault that starves the cleanroom is this
 * function run with loaded filters, and the healthy house is it run clean -
 * so the plant downstairs and the symptom upstairs are one calculation.
 */
export function mauHouse(
  s: MauSettings,
  condition: MauCondition = NOMINAL_MAU_CONDITION,
): { mauDelivery: number; cleanroomPa: number; cleanroomRh: number } {
  const r = runMau(s, condition)
  return { mauDelivery: r.deliveredFraction, cleanroomPa: r.cleanroomPa, cleanroomRh: s.supplyRh }
}
